// Chaves PIX do seller, canvas Zenith `screens/seller/chaves-pix.tsx` (WEB-111, port de
// WEB-62).
//
// Foco da tela: o DOCUMENTO do KYC. Ele é a chave (`spec/09` §4.1, FIX-PIX-LOOKUP), então
// o card do documento vem antes da tabela e o resto é consequência dele.
//
// O estado da adquirente é POR LINHA (`partner_status`, API-29), não mais um `Notice`
// agregado de `counters`: uma chave recusada só aparece na linha dela.
//
// Quem tem `useQuery`/`useMutation` é esta rota; os componentes de `features/pix-keys`
// recebem dado por prop, inclusive o perfil, que antes o dialog de cadastro buscava por
// conta própria (item 4 das "Questões abertas" de FIX-PIX-LOOKUP, agora fechado).
//
// O toggle de modo mora no Shell (WEB-116), não mais no `PageHeader` desta tela.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';
import { profileQueryOptions } from '../api/queries/account.ts';
import {
  type CreatePixKeyBody,
  createPixKey,
  deactivatePixKey,
  PIX_KEYS_QUERY_KEY,
  type PixKey,
  pixKeysQueryOptions,
} from '../api/queries/pix-keys.ts';
import { countersQueryOptions } from '../api/queries/shell.ts';
import { useMode } from '../app/mode.tsx';
import { EmptyState } from '../components/app/empty-state.tsx';
import { ListSkeleton } from '../components/app/list-skeleton.tsx';
import { Notice } from '../components/app/notice.tsx';
import { PageHeader } from '../components/app/page-header.tsx';
import { KeyRound } from '../components/icons/index.tsx';
import { Button } from '../components/ui/button.tsx';
import { Card, CardContent } from '../components/ui/card.tsx';
import { Skeleton } from '../components/ui/skeleton.tsx';
import { CreatePixKeyDrawer } from '../features/pix-keys/create-pix-key-drawer.tsx';
import { KycDocumentCard } from '../features/pix-keys/kyc-document-card.tsx';
import {
  activeCount,
  createBlockedReason,
  createErrorMessage,
  isStepUpCancelled,
  MAX_ACTIVE_PIX_KEYS,
} from '../features/pix-keys/model.ts';
import { type PixKeyRowError, PixKeysTable } from '../features/pix-keys/pix-keys-table.tsx';
import { RemovePixKeyDrawer } from '../features/pix-keys/remove-pix-key-drawer.tsx';

function PixKeys() {
  const { mode } = useMode();
  const queryClient = useQueryClient();
  const keys = useQuery(pixKeysQueryOptions(mode));
  const profile = useQuery(profileQueryOptions(mode));

  const [createOpen, setCreateOpen] = useState(false);
  const [toRemove, setToRemove] = useState<PixKey | undefined>(undefined);
  const [rowError, setRowError] = useState<PixKeyRowError | undefined>(undefined);

  const refresh = async (): Promise<void> => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: [PIX_KEYS_QUERY_KEY] }),
      queryClient.invalidateQueries({ queryKey: countersQueryOptions(mode).queryKey }),
    ]);
  };

  const create = useMutation({
    mutationFn: async (body: CreatePixKeyBody) => createPixKey(mode, body),
    onSuccess: async () => {
      setCreateOpen(false);
      await refresh();
    },
  });

  const remove = useMutation({
    mutationFn: async (key: PixKey) => {
      setRowError(undefined);
      return deactivatePixKey(mode, key.id);
    },
    onSuccess: () => {
      setToRemove(undefined);
    },
    onError: (error, key) => {
      setToRemove(undefined);
      if (!isStepUpCancelled(error)) {
        setRowError({ keyId: key.id, message: createErrorMessage(error) });
      }
    },
    onSettled: refresh,
  });

  const rows = keys.data ?? [];
  const blocked = createBlockedReason(profile.data, rows);
  // Sem documento aprovado o bloqueio se resolve na Verificação.
  const needsKyc = profile.data !== undefined && profile.data.document === null;

  // Frames "Carregando" e "Erro" do canvas: enquanto não há dado nenhum, a tela inteira
  // vira o placeholder ou o aviso, sem documento nem tabela pela metade.
  const isLoading = profile.data === undefined || keys.isPending;
  // Erro só quando não há lista: com o polling de 30 s, um flake num refetch de fundo
  // trocaria a lista carregada por uma tela de falha.
  const isError = keys.data === undefined && keys.error !== null && keys.error !== undefined;

  const openCreate = (): void => {
    create.reset();
    setCreateOpen(true);
  };

  return (
    <>
      <PageHeader
        title="Chaves PIX"
        description="A chave de saque é o documento do seu cadastro, sem consulta a banco"
      >
        {!isLoading && !isError && (
          <Button disabled={blocked !== null} onClick={openCreate}>
            Cadastrar chave
          </Button>
        )}
      </PageHeader>

      {isError ? (
        <Notice
          tone="danger"
          title="Não foi possível carregar as chaves PIX."
          sub="Tente de novo em instantes."
          action={
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                void keys.refetch();
              }}
            >
              Tentar de novo
            </Button>
          }
        />
      ) : profile.data === undefined || keys.isPending ? (
        <div role="status" aria-busy="true" aria-label="Carregando" className="flex flex-col gap-5">
          <Card>
            <CardContent className="flex flex-col gap-3">
              <div className="flex items-center justify-between gap-3">
                <Skeleton className="h-3 w-44" />
                <Skeleton className="h-[22px] w-24 rounded-full" />
              </div>
              <Skeleton className="h-7 w-56 max-w-full" />
              <Skeleton className="h-3 w-72 max-w-full" />
            </CardContent>
          </Card>
          <ListSkeleton rows={3} />
        </div>
      ) : (
        <>
          <KycDocumentCard profile={profile.data} />

          {/* O motivo do bloqueio é TEXTO na tela, não só o `disabled` do botão: um botão
              apagado sem explicação é a tela escondendo a razão (`spec/17` §7 item 12). */}
          {blocked?.kind === 'alert' && (
            <Notice
              tone={blocked.tone}
              title={blocked.reason}
              sub="A chave PIX é o CPF/CNPJ aprovado na verificação, você não escolhe nem digita chave."
              // Conta sem documento aprovado: o aviso leva ao próximo passo em vez de só
              // dizer que está bloqueado.
              {...(needsKyc
                ? {
                    action: (
                      <Button asChild variant="outline" size="sm" className="w-full sm:w-auto">
                        <Link to="/app/settings/kyc">Ir para a verificação</Link>
                      </Button>
                    ),
                  }
                : {})}
            />
          )}

          {rows.length === 0 ? (
            <EmptyState
              icon={KeyRound}
              title="Nenhuma chave PIX cadastrada"
              description="O saque só sai para a chave do documento aprovado no seu KYC. Cadastre-a para liberar o saque."
              // O motivo do bloqueio já está no aviso de cima (`alert`); repetir aqui embaixo
              // era a mesma frase duas vezes na tela.
              {...(blocked === null
                ? { action: <Button onClick={openCreate}>Cadastrar chave</Button> }
                : blocked.kind === 'alert'
                  ? {}
                  : { action: <p className="text-12 text-muted-foreground">{blocked.reason}</p> })}
            />
          ) : (
            // Um cartão só, como a lista de Transações: título e contagem em cima, tabela
            // de ponta a ponta embaixo.
            <Card className="rise-in gap-0 overflow-hidden py-0" style={{ animationDelay: '40ms' }}>
              <div className="flex flex-col gap-0.5 px-5 py-4">
                <h2 className="text-15 font-semibold tracking-[-0.01em] text-foreground">
                  Suas chaves
                </h2>
                <span className="text-13 text-muted-foreground">
                  {activeCount(rows)} de {MAX_ACTIVE_PIX_KEYS} ativas · o saque só sai para chave
                  aprovada pela adquirente
                </span>
                {blocked?.kind === 'quiet' && (
                  <span className="text-13 text-muted-foreground">{blocked.reason}</span>
                )}
              </div>
              <PixKeysTable
                keys={rows}
                onRemove={setToRemove}
                {...(rowError === undefined ? {} : { rowError })}
              />
            </Card>
          )}

          <p className="text-12 text-muted-foreground">
            <span className="font-medium text-foreground">Toda ação aqui exige step-up.</span> Senha
            atual ou código do app autenticador, sem exceção, mesmo com a 2FA desligada.
          </p>
        </>
      )}

      <CreatePixKeyDrawer
        open={createOpen}
        onOpenChange={setCreateOpen}
        profile={profile.data}
        isPending={create.isPending}
        error={create.error}
        onSubmit={(body) => {
          create.mutate(body);
        }}
      />

      <RemovePixKeyDrawer
        pixKey={toRemove}
        onOpenChange={(open) => {
          if (!open) {
            setToRemove(undefined);
          }
        }}
        isPending={remove.isPending}
        onConfirm={(key) => {
          remove.mutate(key);
        }}
      />
    </>
  );
}

export const Route = createFileRoute('/app/settings/pix-keys/')({ component: PixKeys });
